import { useState, useEffect, useRef, useMemo } from "react";
import { useState as useState_, useRef as useRef_, useEffect as useEffect_ } from 'react';
import { api } from "../lib/api";
import { Loader } from "@googlemaps/js-api-loader";

const WEB_MAPS_KEY =
  process.env.REACT_APP_MAPS_WEB_KEY ||
  (typeof window !== "undefined" && window.__MAPS_WEB_KEY) ||
  "";

if (!WEB_MAPS_KEY) {
  console.debug("WEB_MAPS_KEY not found via REACT_APP_MAPS_WEB_KEY or window.__MAPS_WEB_KEY");
}

const DARK_MAP_STYLES = [
  { elementType: "geometry", stylers: [{ color: "#1a1f2b" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#e6eaf2" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#0b0d12" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#2a3144" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#cbd5e1" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0f172a" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#20273a" }] },
  { featureType: "transit", elementType: "geometry", stylers: [{ color: "#20273a" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#334155" }] }
];

// === Number formatting helpers ===
const fmt2 = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtNum = (n) => fmt2.format(Number(n || 0));
const fmtMoney = (n) => `$${fmt2.format(Number(n || 0))}`;

const TooltipButton = ({ content }) => {
  const [open, setOpen] = useState_(false);
  const ref = useRef_();
  // Detect touch device — disables hover for mobile
  const isTouchDevice = typeof window !== "undefined" &&
    ("ontouchstart" in window || navigator.maxTouchPoints > 0);

  useEffect_(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // On mobile, close tooltip on scroll (optional, for UX)
  useEffect_(() => {
    if (!open || !isTouchDevice) return;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    return () => window.removeEventListener("scroll", close, true);
  }, [open, isTouchDevice]);

  return (
    <div
      ref={ref}
      style={{ position: "relative", display: "inline-block", marginLeft: 6 }}
      onMouseEnter={
        !isTouchDevice ? () => setOpen(true) : undefined
      }
      onMouseLeave={
        !isTouchDevice ? () => setOpen(false) : undefined
      }
      onClick={() => setOpen((prev) => !prev)}
    >
      <button
        type="button"
        style={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          backgroundColor: "#2563eb",
          color: "#fff",
          fontSize: 11,
          fontWeight: "bold",
          border: "none",
          cursor: "pointer",
          lineHeight: "18px",
          textAlign: "center",
          padding: 0
        }}
        aria-label="Info"
      >
        ?
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            backgroundColor: "#fff",
            padding: "6px 10px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            borderRadius: 4,
            fontSize: 12,
            color: "#333",
            whiteSpace: "normal",
            width: 220,
            maxWidth: "90vw"
          }}
        >
          {content}
        </div>
      )}
    </div>
  );
};

export default function Calculator() {
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [result, setResult] = useState(null);

  // Find the shortest route among all routes (by distanceMeters)
  const shortestRoute = useMemo(() => {
    if (!result?.routeDebug?.routes?.length) return null;
    return result.routeDebug.routes.reduce((a, b) =>
      a.distanceMeters < b.distanceMeters ? a : b
    );
  }, [result]);

  const [submitting, setSubmitting] = useState(false);
  const [feet, setFeet] = useState("");
  const [pounds, setPounds] = useState("");
  const [leadOpen, setLeadOpen] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [budget, setBudget] = useState("");
  const [notes, setNotes] = useState("");
  const [showBudgetField, setShowBudgetField] = useState(false);
  const [leadError, setLeadError] = useState("");
  const [lastQuerySig, setLastQuerySig] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [isDark, setIsDark] = useState(() => (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches));
  // Consent checkboxes for lead form
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeContact, setAgreeContact] = useState(false);
  const [maintenance, setMaintenance] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  // Track selected interest tiers (multi-select)
  const [selectedTiers, setSelectedTiers] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/config");
        setMaintenance(Boolean(data?.maintenanceMode));
      } catch (e) {
        console.warn("/config load failed", e?.message || e);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me", { withCredentials: true });
        if (data && (data.role === "admin" || data.email)) setIsAdmin(true);
      } catch (_) {
        // not logged in — ignore
      }
    })();
  }, []);

  const originRef = useRef(null);
  const destinationRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const drawnPolylinesRef = useRef([]);
  // Ref for DirectionsRenderer instance for backend-provided route
  const directionsRendererRef = useRef(null);
  const [error, setError] = useState("");
  const hasRoutes = !!(result && result.routeDebug && Array.isArray(result.routeDebug.routes) && result.routeDebug.routes.length);

  const quotePrices = useMemo(() => {
    if (!result || !result.prices) return null;
    return {
      standard: result.prices.standard,
      timeSensitive: result.prices.timeSensitive,
      expedited: result.prices.expedited,
      specialty: result.prices.specialty,
      preloadNextDay: result.prices.preloadNextDay,
    };
  }, [result]);

  const currentSig = useMemo(() => `${origin}|${destination}|${feet}|${pounds}`, [origin, destination, feet, pounds]);

  const showCustom = !!(result && result.custom && lastQuerySig === currentSig);
  const showResult = !!(result && lastQuerySig === currentSig);

  useEffect(() => {
    if (!WEB_MAPS_KEY) {
      console.warn("Maps Web key missing. Set VITE_MAPS_WEB_KEY (Vite) or REACT_APP_MAPS_WEB_KEY (CRA) or window.__MAPS_WEB_KEY.");
      return;
    }
    const loader = new Loader({
      apiKey: WEB_MAPS_KEY,
      libraries: ["places", "geometry"],
    });
    loader
      .load()
      .then(() => {
        if (!window.google || !window.google.maps || !window.google.maps.places) return;
        if (originRef.current) {
          const ac1 = new window.google.maps.places.Autocomplete(originRef.current, {
            fields: ["formatted_address", "geometry", "name", "place_id"]
          });
          ac1.addListener("place_changed", () => {
            const val = originRef.current.value;
            setOrigin(val);
          });
        }
        if (destinationRef.current) {
          const ac2 = new window.google.maps.places.Autocomplete(destinationRef.current, {
            fields: ["formatted_address", "geometry", "name", "place_id"]
          });
          ac2.addListener("place_changed", () => {
            const val = destinationRef.current.value;
            setDestination(val);
          });
        }
      })
      .catch(console.error);
  }, []);

  function clearPolylines() {
    drawnPolylinesRef.current.forEach(p => p.setMap(null));
    drawnPolylinesRef.current = [];
  }

  useEffect(() => {
    // Remove any previous DirectionsRenderer instance for backend-provided route
    if (directionsRendererRef.current) {
      directionsRendererRef.current.setMap(null);
      directionsRendererRef.current = null;
    }

    // If Google is not ready yet, do nothing
    if (!window.google || !window.google.maps) return;

    // Reset map overlays and view BEFORE rendering the new route
    if (mapInstanceRef.current) {
      // Clear previous overlays
      drawnPolylinesRef.current.forEach(poly => poly.setMap(null));
      drawnPolylinesRef.current = [];
      // Optionally reset the map view to default zoom/center
      mapInstanceRef.current.setZoom(10);
      mapInstanceRef.current.setCenter({ lat: 34.0522, lng: -118.2437 }); // Example: Los Angeles
    }

    // If there is no result/routes, just clear existing polylines and keep the map instance
    if (!result || !result.routeDebug || !Array.isArray(result.routeDebug.routes) || !result.routeDebug.routes.length) {
      // Clear any existing polylines from previous result (already done above)
      return;
    }

    // Create map instance once if it doesn't exist
    if (!mapInstanceRef.current && mapRef.current) {
      const isDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        zoom: 8,
        center: { lat: 34.0522, lng: -118.2437 }, // LA default
        mapTypeId: "roadmap",
        styles: isDark ? DARK_MAP_STYLES : null,
      });
    }

    const g = window.google;
    const bounds = new g.maps.LatLngBounds();

    // Draw only the shortest route (by encodedPolyline)
    if (shortestRoute?.encodedPolyline && window.google?.maps?.geometry) {
      const path = window.google.maps.geometry.encoding.decodePath(
        shortestRoute.encodedPolyline
      );

      const polyline = new window.google.maps.Polyline({
        path,
        geodesic: true,
        strokeColor: "#007AFF",
        strokeOpacity: 1.0,
        strokeWeight: 6,
        zIndex: 9999,
      });

      polyline.setMap(mapInstanceRef.current);
      drawnPolylinesRef.current.push(polyline);
      path.forEach(latlng => bounds.extend(latlng));
    }

    // Fit map to polyline bounds
    if (!bounds.isEmpty()) {
      mapInstanceRef.current.fitBounds(bounds);
    }
  }, [result, shortestRoute]);

  useEffect(() => {
    if (!window.matchMedia || !mapInstanceRef.current) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      const isDark = mq.matches;
      mapInstanceRef.current.setOptions({ styles: isDark ? DARK_MAP_STYLES : null });
      // Recolor existing polylines to match theme and selection
      const sel = result?.routeDebug?.selectedIndex ?? -1;
      drawnPolylinesRef.current.forEach((pl, idx) => {
        if (isDark) {
          pl.setOptions({ strokeColor: "#ffffff", strokeOpacity: idx === sel ? 1.0 : 0.5 });
        } else {
          // Clear to default color by setting only opacity
          pl.setOptions({ strokeColor: undefined, strokeOpacity: idx === sel ? 1.0 : 0.6 });
        }
      });
    };
    try {
      mq.addEventListener("change", apply);
    } catch {
      // Safari <14
      mq.addListener(apply);
    }
    // apply once in case map was created before this effect
    apply();
    return () => {
      try { mq.removeEventListener("change", apply); } catch { mq.removeListener(apply); }
    };
  }, [result]);

  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setIsDark(mq.matches);
    try { mq.addEventListener("change", onChange); } catch { mq.addListener(onChange); }
    return () => { try { mq.removeEventListener("change", onChange); } catch { mq.removeListener(onChange); } };
  }, []);

  async function getQuote() {
    setError("");
    // do not clear result to keep map DOM mounted; debug panel will update when new result arrives

    const o = (origin || "").trim();
    const d = (destination || "").trim();
    const f = (feet || "").trim();
    const p = (pounds || "").trim();

    const missing = [];
    if (!o) missing.push("Origin");
    if (!d) missing.push("Destination");
    if (!f) missing.push("Space (feet)");
    if (!p) missing.push("Weight (lbs)");
    if (missing.length) {
      setError(`Please fill out: ${missing.join(", ")}`);
      return;
    }

    const fNum = Number(f);
    const pNum = Number(p);
    if (!Number.isFinite(fNum) || fNum <= 0 || !Number.isFinite(pNum) || pNum <= 0) {
      setError("Feet and pounds must be positive numbers.");
      return;
    }

    try {
      setLoadingQuote(true);
      const { data } = await api.post("/quote", { origin: o, destination: d, feet: fNum, pounds: pNum });
      if (mapInstanceRef.current) {
        mapInstanceRef.current = null;
      }
      clearPolylines();
      setResult(data);
      setLastQuerySig(`${o}|${d}|${fNum}|${pNum}`);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Request failed";
      setError(msg);
    } finally {
      setLoadingQuote(false);
    }
  }

  async function submitLead() {
    if (!result) {
      setLeadError("Please calculate a quote first.");
      return;
    }
    if (!fullName || !email || !phone) {
      setLeadError("Full Name, Email, and Phone are required.");
      return;
    }
    if (!agreeTerms || !agreeContact) {
      setLeadError(!agreeTerms ? "You must accept the Terms & Privacy Policy." : "You must consent to being contacted about your quote.");
      return;
    }
    setSubmitting(true);
    setLeadError("");
    try {
      await api.post("/lead", {
        fullName,
        email,
        phone,
        company,
        origin,
        destination,
        feet: Number(feet),
        pounds: Number(pounds),
        prices: quotePrices || undefined,
        custom: Boolean(result?.custom),
        agreeTerms,
        agreeContact,
        budget: showBudgetField && budget !== "" ? Number(budget) : undefined,
        notes: notes || undefined,
        selected: selectedTiers.length > 0 ? selectedTiers : undefined,
      });
      setLeadOpen(false);
      setFullName(""); setEmail(""); setPhone(""); setCompany("");
      setAgreeTerms(false); setAgreeContact(false);
      alert("Thanks! We’ll be in touch shortly.");
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || "Failed to submit lead";
      setLeadError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (maintenance && !isAdmin) {
    return (
      <div className="container rn-container" style={{ maxWidth: 720, textAlign: "center", padding: 24 }}>
        <div className="rn-header">
          <div className="rn-title">Freight Rate Calculator</div>
          <div className="rn-subtitle">We're performing maintenance</div>
        </div>
        <div className="rn-card" style={{ padding: 20 }}>
          <h3 style={{ marginTop: 0 }}>Maintenance Mode</h3>
          <p>We're updating our systems right now. The calculator will be back shortly.</p>
          <p style={{ fontSize: 13, color: "#6b7280" }}>If you need an immediate quote, please call or email our team.</p>
        </div>
        <footer className="rn-footer" style={{ marginTop: 32, padding: 12, borderTop: "1px solid #e5e7eb", fontSize: 12, textAlign: "center" }}>
          <div style={{ marginBottom: 6 }}>
            By using this app, you agree to our <a href="https://nationwidefreighttransport.com/terms" target="_blank" rel="noreferrer">Terms & Conditions</a> and <a href="https://nationwidefreighttransport.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
          </div>
          <div style={{ marginBottom: 6 }}>
            Services are provided by NFT – Nationwide Freight Transport Inc.
          </div>
          <div>
            © {new Date().getFullYear()} NFT • Web and Mobile Powered by <a href="https://davelcreative.com" target="_blank" rel="noreferrer">Davel Creative Agency</a>
          </div>
        </footer>
      </div>
    );
  }
  return (
    <div className="container rn-container" style={{ maxWidth: 720 }}>
      <div className="rn-header">
        <div className="rn-title">Freight Rate Calculator</div>
        <div className="rn-subtitle">Enter trip details to estimate costs and visualize routes</div>
      </div>
      <div className="rn-card" style={{ display: "grid", gap: 10 }}>
        <div className="rn-subtitle" style={{ fontWeight: 700, marginBottom: 6 }}>Route</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ flex: 1, display: "grid", gap: 8 }}>
            <input
              ref={originRef}
              required
              placeholder="Origin (city, zip or address)"
              value={origin}
              onChange={e => { setOrigin(e.target.value); setError(""); }}
            />
            <input
              ref={destinationRef}
              required
              placeholder="Destination (city, zip or address)"
              value={destination}
              onChange={e => { setDestination(e.target.value); setError(""); }}
            />
          </div>
          <button
            type="button"
            className="btn-plain"
            onClick={() => {
              const o = origin;
              setOrigin(destination);
              setDestination(o);
            }}
            style={{ fontSize: 18, padding: "4px 8px" }}
            aria-label="Swap Origin and Destination"
          >
            ⇅
          </button>
        </div>

        <label>Space Required (feet)</label>
        <input
          required
          inputMode="numeric"
          value={feet}
          onChange={e => { setFeet(e.target.value); setError(""); }}
          placeholder="e.g., 12"
        />
        <label>Total Weight (lbs)</label>
        <input
          required
          inputMode="numeric"
          value={pounds}
          onChange={e => { setPounds(e.target.value); setError(""); }}
          placeholder="e.g., 3500"
        />

        {error && (
          <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", color: "#991B1B", padding: 10, borderRadius: 6 }}>
            {error}
          </div>
        )}

        <div className="rn-actions">
          <button className="btn-primary" onClick={getQuote} disabled={loadingQuote || !origin || !destination || !feet || !pounds}>
            {loadingQuote ? "Loading…" : "Get Quote"}
          </button>
        </div>
      </div>

      {showResult && !result.custom && result.prices && (
        <div className="rn-card rn-result" style={{ marginTop: 16, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div>
              <div className="rn-total">
                {fmtMoney(result.prices.standard)}
                <TooltipButton content="Pickup/Delivery same day or next day depending on booking time" />
              </div>
              <div className="rn-totalSub">
                Estimated Standard Total • 
                {shortestRoute?.distanceMeters && (
                  <> {fmtNum(shortestRoute.distanceMeters / 1609.344)} mi</>
                )}
              </div>
            </div>
            <button
              className="btn-primary"
              onClick={() => { setShowBudgetField(false); setLeadOpen(true); setLeadError(""); }}
              disabled={submitting}
              style={{ background: isDark ? "#16a34a" : "#2563eb", borderColor: isDark ? "#16a34a" : "#2563eb", whiteSpace: "nowrap" }}
            >
              I am interested
            </button>
          </div>
          {/* {result.tier && (
            <div className="rn-muted">Tier: up to {result.tier.maxFeet} ft / {result.tier.maxPounds} lbs (Base Fee: {fmtMoney(result.tier.baseFee)})</div>
          )} */}
            <div className="rn-breakdown">
              <div className="rn-breakdownRow">
                <span className="rn-breakdownKey">
                  Time Sensitive
                  <TooltipButton content="Preferred time/date of pickup and/or delivery" />
                </span>
                <span className="rn-breakdownVal">{fmtMoney(result.prices.timeSensitive)}</span>
              </div>
              <div className="rn-breakdownRow">
                <span className="rn-breakdownKey">
                  Expedited
                  <TooltipButton content="Pickup and Delivery within 2–4 hours" />
                </span>
                <span className="rn-breakdownVal">{fmtMoney(result.prices.expedited)}</span>
              </div>
              <div className="rn-breakdownRow">
                <span className="rn-breakdownKey">
                  Preload/Garaging
                  <TooltipButton content="Must be picked up same day and delivered next day" />
                </span>
                <span className="rn-breakdownVal">{fmtMoney(result.prices.preloadNextDay)}</span>
              </div>
              <div className="rn-breakdownRow">
                <span className="rn-breakdownKey">
                  Specialty
                  <TooltipButton content="Oversized, After-Hours, Weekend & Holiday deliveries" />
                </span>
                <span className="rn-breakdownVal">{fmtMoney(result.prices.specialty)}</span>
              </div>
            </div>
            <div style={{ marginTop: 4, textAlign: 'left' }}>
              <button
                type="button"
                className="btn-plain"
                onClick={() => { setShowBudgetField(true); setLeadOpen(true); setLeadError(""); }}
                style={{
                  fontSize: 12,
                  padding: "6px 12px",
                  borderRadius: 9999,
                  border: `1px solid ${isDark ? "#60A5FA" : "#93C5FD"}`,
                  background: isDark ? "rgba(37,99,235,0.18)" : "#EFF6FF",
                  color: isDark ? "#DBEAFE" : "#1E3A8A",
                  cursor: "pointer",
                  textDecoration: "none",
                  fontWeight: 600
                }}
              >
                Have a budget in mind?
              </button>
            </div>
          <div className="rn-smallNote">Prices are estimates and may vary based on final routing and availability.</div>
        </div>
      )}

      {showCustom && !result.prices && (
        <div className="rn-card" style={{ marginTop: 16, textAlign: 'right' }}>
          <button
            className="btn-primary"
            onClick={() => { setLeadOpen(true); setLeadError(""); }}
            disabled={submitting}
            style={{ background: isDark ? "#16a34a" : "#2563eb", borderColor: isDark ? "#16a34a" : "#2563eb", whiteSpace: "nowrap" }}
          >
            I am interested
          </button>
        </div>
      )}
      {showCustom && (
        <div className="alert alert-warn rn-sep" style={{ marginTop: 16 }}>
          <div><b>This request exceeds configured tiers. Please submit for a custom quote.</b></div>
          <div style={{ marginTop: 6, fontSize: 12 }}>
            If you’re interested, click <i>I am interested</i> above to send your details and we’ll follow up with a custom rate.
          </div>
        </div>
      )}

      {showResult && (
        <div className="map-shell">
          <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
        </div>
      )}


      {leadOpen && (
        <div className="modal-overlay" style={{ padding: 12 }}>
          <div
            className="modal"
            style={{
              maxHeight: '85vh',
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              width: 'min(640px, calc(100vw - 24px))'
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}>Contact details</h3>
              <button
                className="btn-plain"
                onClick={() => { setLeadOpen(false); setShowBudgetField(false); }}
                aria-label="Close"
                style={{ fontSize: 18 }}
              >
                ×
              </button>
            </div>

            <div style={{ display: "grid", gap: 8 }}>
              <input required placeholder="Full Name" value={fullName} onChange={e => setFullName(e.target.value)} />
              <input required placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} />
              <input required placeholder="Phone" value={phone} onChange={e => setPhone(e.target.value)} />
              <input placeholder="Company (Optional)" value={company} onChange={e => setCompany(e.target.value)} />
              {showBudgetField && (
                <>
                  <label style={{ fontWeight: 600, marginTop: 6 }}>Have a budget in mind? (optional)</label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 14, opacity: 0.8 }}>$</span>
                    <input
                      placeholder="e.g., 450"
                      inputMode="decimal"
                      value={budget}
                      onChange={e => setBudget(e.target.value)}
                      style={{ flex: 1 }}
                    />
                  </div>
                </>
              )}
              <label style={{ fontWeight: 600, marginTop: 6 }}>Additional Notes (Optional)</label>
              <textarea
                rows={3}
                placeholder="Any timing constraints, loading dock info, after-hours needs, oversized items, etc."
                value={notes}
                onChange={e => setNotes(e.target.value)}
                style={{ width: '100%' }}
              />

              {/* Interested in (optional) multi-select */}
              <div>
                <label style={{ fontWeight: 600, marginTop: 6, display: 'block' }}>Interested in (optional)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: 4 }}>
                  {[
                    { label: 'Standard', value: 'standard' },
                    { label: 'Time Sensitive', value: 'timeSensitive' },
                    { label: 'Expedited', value: 'expedited' },
                    { label: 'Preload/Garaging', value: 'preloadNextDay' },
                    { label: 'Specialty', value: 'specialty' },
                  ].map(opt => {
                    const active = selectedTiers.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() =>
                          setSelectedTiers(prev =>
                            prev.includes(opt.value)
                              ? prev.filter(v => v !== opt.value)
                              : [...prev, opt.value]
                          )
                        }
                        style={{
                          padding: '4px 10px',
                          fontSize: 13,
                          borderRadius: 16,
                          border: `1px solid ${active ? '#2563eb' : '#ccc'}`,
                          backgroundColor: active ? '#2563eb' : '#fff',
                          color: active ? '#fff' : '#000',
                          cursor: 'pointer'
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Consent checkboxes */}
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13 }}>
                <input type="checkbox" checked={agreeTerms} onChange={e => setAgreeTerms(e.target.checked)} />
                <span>
                  By providing my contact information, I confirm I’ve read and agree to the
                  <a href="/terms" target="_blank" rel="noreferrer" style={{ marginLeft: 4 }}>Terms & Conditions</a>
                  {" "}and
                  <a href="/privacy" target="_blank" rel="noreferrer" style={{ marginLeft: 4 }}>Privacy Policy</a>.
                </span>
              </label>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13 }}>
                <input type="checkbox" checked={agreeContact} onChange={e => setAgreeContact(e.target.checked)} />
                <span>
                  I agree to receive follow‑ups about my quote via phone and/or email.
                   <p style={{ fontSize: 8 }}>By providing my contact information, I authorize Nationwide Freight Transport Inc. (NFT) to contact me about my inquiry. Consent is not a condition of purchase. Message and data rates may apply.</p>
                </span>
              </label>

              {quotePrices && (
                <div className="rn-card" style={{ fontSize: 12 }}>
                  <div><b>Quote Snapshot</b></div>
                  <div>Standard: {fmtMoney(quotePrices.standard)}</div>
                  <div>Time Sensitive: {fmtMoney(quotePrices.timeSensitive)}</div>
                  <div>Expedited: {fmtMoney(quotePrices.expedited)}</div>
                  <div>Preload/Garaging: {fmtMoney(quotePrices.preloadNextDay)}</div>
                  <div>Specialty: {fmtMoney(quotePrices.specialty)}</div>
                </div>
              )}

              {leadError && (
                <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", color: "#991B1B", padding: 8, borderRadius: 6 }}>{leadError}</div>
              )}

              <div className="rn-actions" style={{ justifyContent: "flex-end" }}>
                <button className="btn-secondary" onClick={() => setLeadOpen(false)} type="button">Cancel</button>
                <button
                  className="btn-primary"
                  onClick={submitLead}
                  disabled={submitting || !fullName || !email || !phone || !agreeTerms || !agreeContact}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="rn-card" style={{ marginTop: 40, marginBottom: 16, padding: 16, borderRadius: 12, border: '1px solid #e5e7eb' }}>
        <div style={{ color: isDark ? "#f3f4f6" : "#111827", fontSize: 14, lineHeight: "20px" }}>
          <strong>Note:</strong> We offer Weekend, After-Hours, and Oversized Freight Delivery services. Rates for these services may vary. Please contact Dispatch directly for an accurate quote.
        </div>
        <a
          href="tel:8884194591"
          style={{ display: "flex", alignItems: "center", marginTop: 8, color: isDark ? "#93c5fd" : "#2563eb", fontWeight: 700, fontSize: 16, textDecoration: "none" }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" style={{ marginRight: 6 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.6a1 1 0 01.92.606l1.2 2.788a1 1 0 01-.217 1.09l-1.35 1.35a11.05 11.05 0 005.172 5.172l1.35-1.35a1 1 0 011.09-.217l2.788 1.2a1 1 0 01.606.92V19a2 2 0 01-2 2h-1C8.268 21 3 15.732 3 9V5z" />
          </svg>
          888-419-4591
        </a>
      </div>
      <footer className="rn-footer" style={{ marginTop: 32, padding: 12, borderTop: "1px solid #e5e7eb", fontSize: 12, textAlign: "center" }}>
        <div style={{ marginBottom: 6 }}>
          By using this app, you agree to our <a href="https://nationwidefreighttransport.com/terms" target="_blank" rel="noreferrer">Terms & Conditions</a> and <a href="https://nationwidefreighttransport.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
        </div>
        <div style={{ marginBottom: 6 }}>
          Services are provided by NFT – Nationwide Freight Transport Inc.
        </div>
        <div>
          © {new Date().getFullYear()} NFT • Web and Mobile Powered by <a href="https://davelcreative.com" target="_blank" rel="noreferrer">Davel Creative Agency</a>
        </div>
      </footer>
    </div>
  );
}